import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { CellProps } from 'react-datasheet-grid'

// 목록에서 고르는 칸 (태그 타입·디바이스 프로토콜) — 브라우저 기본 <select> 는 그리드가 클릭을 먼저 가져가 잘 안 열려서 목록을 직접 그린다.
// 칸을 고른 뒤 한 번 더 클릭·Enter·F2 로 열고, ↑↓ 로 고르고 Enter/Tab 으로 확정, 글자를 치면 그 글자로 시작하는 항목으로,
// Esc 는 취소. 빈 값은 고를 때만 표시한다 (placeholder 를 주면 늘 흐리게 보인다).
// 클릭으로 여는 건 그리드 편집 상태와 따로 둔다 — 그리드는 두 번째 클릭을 편집 시작으로 보지만, 버튼을 떼기 전에
// 마우스가 조금만 움직여도 범위 선택 드래그로 보고 편집을 끝내 버려서 목록이 바로 닫힌다.
// 그래서 선택된 칸을 누르면 그 클릭을 그리드에 넘기지 않고 직접 연다 (Enter·F2 는 그대로 그리드 편집 = focus).
export interface ChoiceOption { value: string; label: string }
export interface ChoiceData { options: ChoiceOption[]; fallback?: number; placeholder?: string }

export default function ChoiceCell({ rowData, setRowData, focus, active, stopEditing, columnData }: CellProps<string, ChoiceData>) {
  const { options, fallback = 0, placeholder } = columnData
  const ref = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)
  const [box, setBox] = useState<{ left: number; top: number; width: number } | null>(null)
  const [clicked, setClicked] = useState(false) // 클릭으로 연 목록
  const open = focus || (clicked && active)
  const current = options.find(o => o.value === rowData)

  // 열릴 때 지금 값에 맞추고 칸 아래에 띄운다
  useEffect(() => {
    if (!open || !ref.current) return
    const r = ref.current.getBoundingClientRect()
    const i = options.findIndex(o => o.value === rowData)
    const raf = requestAnimationFrame(() => {
      setIndex(i >= 0 ? i : fallback)
      setBox({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 140) })
    })
    return () => cancelAnimationFrame(raf)
  }, [open, rowData, options, fallback])

  // 다른 칸으로 옮기면 클릭으로 연 목록은 닫는다
  useEffect(() => {
    if (!active && clicked) {
      const raf = requestAnimationFrame(() => setClicked(false))
      return () => cancelAnimationFrame(raf)
    }
  }, [active, clicked])

  // 클릭으로 연 목록 — 바깥을 누르면 닫는다 (목록·칸 안의 누름은 거기서 멈춰 여기까지 오지 않는다)
  useEffect(() => {
    if (!clicked) return
    const onDown = () => setClicked(false)
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [clicked])

  const close = (nextRow = false) => {
    if (focus) stopEditing({ nextRow })
    setClicked(false)
  }
  const choose = (i: number, nextRow = false) => {
    setRowData(options[i].value)
    close(nextRow)
  }

  // 선택된 칸을 누르면 열기/닫기 — 그리드로 보내지 않는다
  const onMouseDown = (e: React.MouseEvent) => {
    if (!active || e.button !== 0 || e.shiftKey || e.ctrlKey || e.metaKey) return
    e.preventDefault()
    e.stopPropagation()
    if (open) close()
    else setClicked(true)
  }

  // 열려 있는 동안 키는 목록이 먼저 (그리드보다 앞, window capture)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing) return
      let handled = true
      if (e.key === 'ArrowDown') setIndex(i => Math.min(i + 1, options.length - 1))
      else if (e.key === 'ArrowUp') setIndex(i => Math.max(i - 1, 0))
      else if (e.key === 'Enter' || e.key === 'Tab') choose(index, e.key === 'Enter')
      else if (e.key === 'Escape') close()
      else if (e.key.length === 1 && /[a-z]/i.test(e.key)) {
        // 같은 글자를 또 치면 그 글자로 시작하는 다음 항목으로
        const starts = options.map((o, i) => [o.label, i] as const).filter(([t]) => t.toLowerCase().startsWith(e.key.toLowerCase()))
        const next = starts.find(([, i]) => i > index) ?? starts[0]
        if (next) setIndex(next[1])
      } else handled = false
      if (handled) { e.preventDefault(); e.stopImmediatePropagation() }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  })

  const empty = !rowData && !open && !active
  return (
    <div ref={ref} onMouseDown={onMouseDown} className="w-full h-full flex items-center px-2 text-xs cursor-pointer select-none min-w-0">
      {empty
        ? placeholder && <span className="text-gray-400 truncate">{placeholder}</span>
        : <span className={`truncate ${current ? '' : 'text-red-600'}`}>{current?.label ?? (rowData || '-')}</span>}
      {(active || open) && (
        <svg className="w-3 h-3 ml-auto shrink-0 text-gray-400" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {open && box && createPortal(
        <div className="choice-list fixed z-[60] max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-surface shadow-lg py-1 text-xs"
          style={{ left: box.left, top: box.top, width: box.width }}
          onMouseDown={e => { e.preventDefault(); e.stopPropagation() /* 그리드가 바깥 클릭으로 편집을 끝내지 않게 */ }}>
          {options.map((o, i) => (
            <div key={o.value} onMouseEnter={() => setIndex(i)} onClick={() => choose(i)}
              className={`px-3 py-1.5 cursor-pointer flex items-center whitespace-nowrap ${i === index ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}>
              {o.label}
              {o.value === rowData && <span className="ml-auto pl-2 text-gray-400">✓</span>}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}
